import React from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Star } from 'lucide-react';

const WHATSAPP_NUMBER = '917669921530';

const plans = [
  {
    name: 'Basic',
    price: '4,999',
    period: '/month',
    badge: null,
    best: 'Small businesses getting started',
    features: [
      'Basic Instagram content strategy',
      '8-10 posts/reels per month guidance',
      'Caption + hashtag support',
      'Monthly growth suggestions',
    ],
    cta: 'Get Started',
    message: "Hi, I'm interested in your ₹4,999/month Basic plan.",
    accent: false,
  },
  {
    name: 'Growth',
    price: '11,999',
    period: '/month',
    badge: 'Most Popular',
    best: 'Businesses wanting consistent growth',
    features: [
      'Complete content strategy',
      'Reels planning & scripting',
      'Posting schedule & guidance',
      'Influencer recommendations',
      'Profile optimization',
      'Monthly performance review',
    ],
    cta: 'Start Growing',
    message: "Hi, I'm interested in your ₹11,999/month Growth plan.",
    accent: true,
  },
  {
    name: 'Pro',
    price: '19,999',
    period: '/month',
    badge: null,
    best: 'Serious business growth',
    features: [
      'Full Instagram management',
      'Content strategy + execution',
      'Influencer campaign handling',
      'Growth optimization & analytics',
      'Priority support',
    ],
    cta: 'Scale My Instagram',
    message: "Hi, I'm interested in your ₹19,999/month Pro plan.",
    accent: false,
  },
];

export const PricingSection = () => {
  const openWhatsApp = (message) => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <span className="font-accent text-slate-400 mb-4 block" data-testid="pricing-label">
            Pricing
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-tight mb-4"
            data-testid="pricing-headline"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Simple Pricing.{' '}
            <span className="font-medium">Real Growth.</span>
          </h2>
          <p className="text-base md:text-lg text-slate-500 max-w-xl mx-auto mb-3">
            Start growing your Instagram without breaking the bank.
          </p>
        </motion.div>

        {/* Pricing hook */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-center mb-16"
          data-testid="pricing-hook"
        >
          <span
            className="inline-block bg-slate-900 text-white text-sm md:text-base font-medium px-6 py-3 rounded-full"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Plans starting at just ₹4,999/month
          </span>
        </motion.div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-5 items-stretch">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`relative rounded-3xl p-8 md:p-9 flex flex-col transition-all duration-300 ${
                plan.accent
                  ? 'bg-slate-900 text-white ring-2 ring-slate-900 md:scale-[1.03]'
                  : 'bg-slate-50 border border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
            >
              {/* Badge */}
              {plan.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1.5 bg-white text-slate-900 text-xs font-semibold px-4 py-1.5 rounded-full border border-slate-200 shadow-sm">
                    <Star size={12} className="fill-amber-400 text-amber-400" />
                    {plan.badge}
                  </span>
                </div>
              )}

              {/* Plan name & best for */}
              <div className="mb-6">
                <h3
                  className={`text-lg font-medium mb-1 ${plan.accent ? 'text-white' : 'text-slate-900'}`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {plan.name}
                </h3>
                <p className={`text-xs ${plan.accent ? 'text-slate-400' : 'text-slate-500'}`}>
                  Best for: {plan.best}
                </p>
              </div>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className={`text-sm ${plan.accent ? 'text-slate-400' : 'text-slate-500'}`}>₹</span>
                  <span
                    className={`text-4xl md:text-5xl font-light tracking-tight ${plan.accent ? 'text-white' : 'text-slate-900'}`}
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {plan.price}
                  </span>
                  <span className={`text-sm ${plan.accent ? 'text-slate-400' : 'text-slate-500'}`}>
                    {plan.period}
                  </span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                      plan.accent ? 'bg-white/10' : 'bg-slate-200'
                    }`}>
                      <Check size={12} className={plan.accent ? 'text-white' : 'text-slate-600'} strokeWidth={2.5} />
                    </div>
                    <span className={`text-sm leading-snug ${plan.accent ? 'text-slate-300' : 'text-slate-600'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={() => openWhatsApp(plan.message)}
                data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                className={`w-full rounded-full px-6 py-3.5 text-sm font-medium transition-colors inline-flex items-center justify-center gap-2 group ${
                  plan.accent
                    ? 'bg-white text-slate-900 hover:bg-slate-100'
                    : 'bg-slate-900 text-white hover:bg-slate-800'
                }`}
              >
                {plan.cta}
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Trust elements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-14 text-center space-y-3"
          data-testid="pricing-trust"
        >
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-slate-500">
            <span>No long-term contracts. Cancel anytime.</span>
            <span className="hidden md:inline">·</span>
            <span>Designed specifically for local businesses.</span>
          </div>
          <p className="text-xs font-medium text-amber-600 tracking-wide">
            Limited onboarding slots each month
          </p>
        </motion.div>
      </div>
    </section>
  );
};
